import { type GraphEdge, type GraphNode, type PortType, type NodeDefBase } from '../graph/nodeDefs.js';

import {
  edgeKey,
  type EdgeVisualStateController,
  type EdgeStateName
} from './edgeVisualState.js';
import type { PendingConnection } from '../editor/connectionMutations.js';

const TYPE_COLOR_VARS: Record<PortType, string> = {
  float: '--type-float',
  vec2: '--type-vec2',
  vec3: '--type-vec3',
  vec4: '--type-vec4',
  bool: '--type-bool',
  string: '--type-string',
  strings: '--type-string',
  flow: '--type-flow',
  collection: '--type-collection',
  record: '--type-record',
  bounds: '--type-bounds',
  transform: '--type-transform',
  shape: '--type-shape',
  size: '--type-vec2',
  rects: '--type-vec4',
};

function readTypeColor(type: PortType): string {
  return getComputedStyle(document.documentElement).getPropertyValue(TYPE_COLOR_VARS[type]).trim() || 'var(--text-dim)';
}

export interface EdgeVisual {
  key: string;
  type: PortType | null;
  color: string;
  state: EdgeStateName;
  dimmed: boolean;
}

function edgeType(edge: GraphEdge, nodes: GraphNode[], nodeDefs: Record<string, NodeDefBase>): PortType | null {
  const fromNode = nodes.find(node => node.id === edge.fromNode);
  if (!fromNode) return null;
  const def = nodeDefs[fromNode.type];
  if (!def) return null;
  const outputs = typeof def.outputs === 'function' ? def.outputs(fromNode) : def.outputs;
  return outputs[edge.fromPort]?.type ?? null;
}

export function getEdgeVisual(
  visualState: EdgeVisualStateController,
  edge: GraphEdge,
  nodes: GraphNode[],
  nodeDefs: Record<string, NodeDefBase>,
): EdgeVisual {
  const key = edgeKey(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort);
  const type = edgeType(edge, nodes, nodeDefs);
  let stateName: EdgeStateName = 'default';

  if (visualState.getCutPreviewKeys().has(key)) stateName = 'cut-preview';
  else if (visualState.getDetachPreviewEdgeKey() === key) stateName = 'reconnect';
  else if (visualState.getHoveredEdgeKey() === key) stateName = 'hover';

  return {
    key,
    type,
    color: type ? readTypeColor(type) : 'var(--text-dim)',
    state: stateName,
    dimmed: false
  };
}

export function getPendingConnectionVisual(
  pendingConn: PendingConnection | null,
  nodes: GraphNode[],
  nodeDefs: Record<string, NodeDefBase>,
): { color: string; state: 'pending' | 'reconnect' } {
  if (!pendingConn) return { color: 'var(--text-dim)', state: 'pending' };

  const nodeId = pendingConn.isFromOut ? pendingConn.fromNode : pendingConn.toNode;
  const portIdx = pendingConn.isFromOut ? pendingConn.fromPort : pendingConn.toPort;
  const io: 'outputs' | 'inputs' = pendingConn.isFromOut ? 'outputs' : 'inputs';
  const node = nodes.find(candidate => candidate.id === nodeId);
  if (!node || portIdx == null) return { color: 'var(--text-dim)', state: pendingConn.sticky ? 'reconnect' : 'pending' };
  const def = nodeDefs[node.type];
  if (!def) return { color: 'var(--text-dim)', state: pendingConn.sticky ? 'reconnect' : 'pending' };
  const ports = io === 'outputs'
    ? (typeof def.outputs === 'function' ? def.outputs(node) : def.outputs)
    : (typeof def.inputs === 'function' ? def.inputs(node) : def.inputs);
  const type = ports[portIdx]?.type ?? null;

  return {
    color: type ? readTypeColor(type) : 'var(--text-dim)',
    state: pendingConn.sticky ? 'reconnect' : 'pending'
  };
}
